import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { userService, departmentService } from '../../services/api';
import './Admin.css';

const ROLES = ['employee', 'admin'];
const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Temporary'];

const EmployeeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Account
    email: '',
    password: '',
    role: 'employee',

    // Personal
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    address: '',

    // Employment
    jobTitle: '',
    departmentId: '',
    managerId: '',
    employmentType: '',
    joinDate: '',
    endDate: '',

    // Emergency
    emergencyContactName: '',
    emergencyContactPhone: '',

    // Other
    niNumber: '',
  });

  useEffect(() => {
    fetchDepartmentsAndManagers();
    if (isEditMode) {
      fetchEmployee();
    }
  }, []);

  const fetchDepartmentsAndManagers = async () => {
    try {
      const [deptRes, usersRes] = await Promise.all([
        departmentService.getAll({ limit: 1000 }),
        userService.getAll({ limit: 1000 }),
      ]);
      setDepartments(deptRes.data || []);
      setManagers(usersRes.data.users || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const fetchEmployee = async () => {
    try {
      const response = await userService.getById(id);
      const emp = response.data;
      setFormData({
        email: emp.email || '',
        password: '',
        role: emp.role || 'employee',
        firstName: emp.firstName || '',
        lastName: emp.lastName || '',
        phone: emp.phone || '',
        dateOfBirth: emp.dateOfBirth ? emp.dateOfBirth.split('T')[0] : '',
        address: emp.address || '',
        jobTitle: emp.jobTitle || '',
        departmentId: emp.departmentId || '',
        managerId: emp.managerId || '',
        employmentType: emp.employmentType || '',
        joinDate: emp.joinDate ? emp.joinDate.split('T')[0] : '',
        endDate: emp.endDate ? emp.endDate.split('T')[0] : '',
        emergencyContactName: emp.emergencyContactName || '',
        emergencyContactPhone: emp.emergencyContactPhone || '',
        niNumber: emp.nationalInsuranceNumber || '',
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load employee');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!isEditMode && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData.departmentId) {
      newErrors.departmentId = 'Department is required';
    }

    if (!formData.joinDate) {
      newErrors.joinDate = 'Join date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        email: formData.email,
        role: formData.role,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || null,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : null,
        address: formData.address || null,
        jobTitle: formData.jobTitle,
        departmentId: formData.departmentId,
        managerId: formData.managerId || null,
        employmentType: formData.employmentType,
        joinDate: new Date(formData.joinDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        emergencyContactName: formData.emergencyContactName || null,
        emergencyContactPhone: formData.emergencyContactPhone || null,
        nationalInsuranceNumber: formData.niNumber || null,
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      if (isEditMode) {
        await userService.update(id, payload);
      } else {
        await userService.create(payload);
      }

      navigate('/admin/employees');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save employee');
      console.error('Error saving employee:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <button className="btn btn-ghost" onClick={() => navigate('/admin/employees')}>
        <FiArrowLeft /> Back to Employees
      </button>

      <div className="form-header">
        <h1>{isEditMode ? 'Edit Employee' : 'Add Employee'}</h1>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <form onSubmit={handleSubmit} className="employee-form">
        {/* Account Section */}
        <div className="form-section">
          <h2 className="section-title">Account Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isEditMode}
                className={errors.email ? 'input-error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className={errors.role ? 'input-error' : ''}
              >
                {ROLES.map(r => (
                  <option key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
              {errors.role && <span className="error-message">{errors.role}</span>}
            </div>
          </div>

          {!isEditMode && (
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Minimum 6 characters"
                className={errors.password ? 'input-error' : ''}
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
          )}

          {isEditMode && (
            <div className="form-group">
              <label htmlFor="password">New Password (leave blank to keep current)</label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                className={errors.password ? 'input-error' : ''}
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
          )}
        </div>

        {/* Personal Section */}
        <div className="form-section">
          <h2 className="section-title">Personal Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? 'input-error' : ''}
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={errors.lastName ? 'input-error' : ''}
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth</label>
              <input
                id="dateOfBirth"
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input
              id="address"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Employment Section */}
        <div className="form-section">
          <h2 className="section-title">Employment Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="jobTitle">Job Title *</label>
              <input
                id="jobTitle"
                type="text"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                className={errors.jobTitle ? 'input-error' : ''}
              />
              {errors.jobTitle && <span className="error-message">{errors.jobTitle}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="departmentId">Department *</label>
              <select
                id="departmentId"
                name="departmentId"
                value={formData.departmentId}
                onChange={handleChange}
                className={errors.departmentId ? 'input-error' : ''}
              >
                <option value="">Select a department...</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
              {errors.departmentId && <span className="error-message">{errors.departmentId}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="managerId">Manager</label>
              <select
                id="managerId"
                name="managerId"
                value={formData.managerId}
                onChange={handleChange}
              >
                <option value="">Select a manager...</option>
                {managers.map(mgr => (
                  <option key={mgr.id} value={mgr.id}>
                    {mgr.firstName} {mgr.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="employmentType">Employment Type</label>
              <select
                id="employmentType"
                name="employmentType"
                value={formData.employmentType}
                onChange={handleChange}
              >
                <option value="">Select employment type...</option>
                {EMPLOYMENT_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="joinDate">Join Date *</label>
              <input
                id="joinDate"
                type="date"
                name="joinDate"
                value={formData.joinDate}
                onChange={handleChange}
                className={errors.joinDate ? 'input-error' : ''}
              />
              {errors.joinDate && <span className="error-message">{errors.joinDate}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <input
                id="endDate"
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Emergency Section */}
        <div className="form-section">
          <h2 className="section-title">Emergency Contact</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="emergencyContactName">Contact Name</label>
              <input
                id="emergencyContactName"
                type="text"
                name="emergencyContactName"
                value={formData.emergencyContactName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="emergencyContactPhone">Contact Phone</label>
              <input
                id="emergencyContactPhone"
                type="tel"
                name="emergencyContactPhone"
                value={formData.emergencyContactPhone}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Other Section */}
        <div className="form-section">
          <h2 className="section-title">Other</h2>
          <div className="form-group">
            <label htmlFor="niNumber">NI Number</label>
            <input
              id="niNumber"
              type="text"
              name="niNumber"
              value={formData.niNumber}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/employees')}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <div className="inline-spinner"></div> Saving...
              </>
            ) : (
              <>
                <FiSave /> Save Employee
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeForm;
